import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Paper,
    Chip,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Collapse,
    CircularProgress,
    Alert,
    Autocomplete,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
} from '@mui/material';
import {
    Checkroom as VystrojIcon,
    Add as AddIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
    OpenInNew as OpenIcon,
    Print as PrintIcon,
} from '@mui/icons-material';
import {
    useVystrojniKartaQuery,
    useVydejInventarPolozkuMutation,
    useVratInventarPolozkuMutation,
    useAllInventarPolozkyQuery,
    useDownloadVystrojniKartaPdfMutation,
} from '../../api/JsdhNotifikaceService/service/InventarMutations';
import type { InventarPolozka, VydejInventarPolozkyRequest } from '../../api/JsdhNotifikaceService/generated/models/Inventar';
import dayjs from 'dayjs';
import 'dayjs/locale/cs';

dayjs.locale('cs');

const formatDate = (d?: string) => d ? dayjs(d).format('DD.MM.YYYY') : '—';

interface VystrojniKartaSectionProps {
    userId: number;
}

const VystrojniKartaSection: React.FC<VystrojniKartaSectionProps> = ({ userId }) => {
    const navigate = useNavigate();
    const { data: karta, isLoading } = useVystrojniKartaQuery(userId);
    const [historieOpen, setHistorieOpen] = useState(false);
    const [vydejDialogOpen, setVydejDialogOpen] = useState(false);
    const [vraceniPolozkaId, setVraceniPolozkaId] = useState<number | null>(null);

    // Výdej form
    const [vydejPolozkaId, setVydejPolozkaId] = useState<number | null>(null);
    const [vydejDatum, setVydejDatum] = useState(dayjs().format('YYYY-MM-DD'));
    const [vydejStav, setVydejStav] = useState('NOVA');

    // Vrácení form
    const [vraceniDatum, setVraceniDatum] = useState(dayjs().format('YYYY-MM-DD'));
    const [vraceniStav, setVraceniStav] = useState('OK');

    const { data: volnePolozky } = useAllInventarPolozkyQuery(undefined, { enabled: vydejDialogOpen });
    const vydejMutation = useVydejInventarPolozkuMutation();
    const vraceniMutation = useVratInventarPolozkuMutation();
    const downloadKartaPdfMutation = useDownloadVystrojniKartaPdfMutation();

    const volneProVydej = volnePolozky?.filter(p => p.prirazeniTyp === 'STANICE' && p.stav === 'AKTIVNI') ?? [];

    const handleVydej = () => {
        if (!vydejPolozkaId) return;
        vydejMutation.mutate({
            polozkaId: vydejPolozkaId,
            data: { uzivatelId: userId, datumVydeje: vydejDatum, stavPriVydeji: vydejStav },
        }, {
            onSuccess: () => {
                setVydejDialogOpen(false);
                setVydejPolozkaId(null);
                setVydejStav('NOVA');
            },
        });
    };

    const handleVraceni = () => {
        if (!vraceniPolozkaId) return;
        vraceniMutation.mutate({
            polozkaId: vraceniPolozkaId,
            data: { datumVraceni: vraceniDatum, stavPriVraceni: vraceniStav },
        }, {
            onSuccess: () => {
                setVraceniPolozkaId(null);
            },
        });
    };

    if (isLoading) {
        return (
            <Paper sx={{ p: { xs: 2, sm: 3 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <VystrojIcon />
                    <Typography variant="h6">Výstrojní karta</Typography>
                </Box>
                <CircularProgress size={24} />
            </Paper>
        );
    }

    return (
        <>
            <Paper sx={{ p: { xs: 2, sm: 3 } }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <VystrojIcon />
                        <Typography variant="h6">
                            Výstrojní karta
                            {karta && karta.pocetAktivnich > 0 && (
                                <Chip label={karta.pocetAktivnich} size="small" color="primary" sx={{ ml: 1 }} />
                            )}
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button size="small" startIcon={<PrintIcon />} onClick={() => downloadKartaPdfMutation.mutate(userId)}>
                            Tisk PDF
                        </Button>
                        <Button size="small" startIcon={<AddIcon />} onClick={() => setVydejDialogOpen(true)}>
                            Vydat prostředek
                        </Button>
                    </Box>
                </Box>

                <Alert severity="info" sx={{ mb: 2 }}>
                    Beta verze — výstrojní karta slouží k otestování funkcí.
                </Alert>

                {/* Aktivní položky */}
                {karta && karta.aktivniPolozky.length > 0 ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        {karta.aktivniPolozky.map(p => (
                            <Box
                                key={p.id}
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    p: 1.5,
                                    borderRadius: 1,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    '&:hover': { bgcolor: 'action.hover' },
                                }}
                            >
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography variant="subtitle2" noWrap>
                                        {p.nazev}
                                        {p.velikost && <Typography component="span" variant="body2" color="text.secondary"> (vel. {p.velikost})</Typography>}
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                                        <Chip label={p.kategorieNazev} size="small" variant="outlined" />
                                        {p.serioveCislo && <Chip label={`SN: ${p.serioveCislo}`} size="small" variant="outlined" />}
                                        {p.inventarniCislo && <Chip label={`INV: ${p.inventarniCislo}`} size="small" variant="outlined" />}
                                    </Box>
                                </Box>
                                <Box sx={{ display: 'flex', gap: 1, ml: 1, alignItems: 'center' }}>
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        color="warning"
                                        onClick={() => setVraceniPolozkaId(p.id)}
                                    >
                                        Vrátit
                                    </Button>
                                    <Button
                                        size="small"
                                        onClick={() => navigate(`/admin/inventar/${p.id}`)}
                                        startIcon={<OpenIcon />}
                                    >
                                        Detail
                                    </Button>
                                </Box>
                            </Box>
                        ))}
                    </Box>
                ) : (
                    <Typography color="text.secondary" variant="body2">
                        Žádné aktuálně svěřené prostředky.
                    </Typography>
                )}

                {/* Historie (collapsible) */}
                {karta && karta.pocetHistorickych > 0 && (
                    <Box sx={{ mt: 2 }}>
                        <Button
                            size="small"
                            onClick={() => setHistorieOpen(!historieOpen)}
                            endIcon={historieOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        >
                            Historie ({karta.pocetHistorickych})
                        </Button>
                        <Collapse in={historieOpen}>
                            <TableContainer sx={{ mt: 1 }}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Položka</TableCell>
                                            <TableCell>Vydáno</TableCell>
                                            <TableCell>Vráceno</TableCell>
                                            <TableCell>Stav</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {karta.historiePolozek.map(h => (
                                            <TableRow key={h.id}>
                                                <TableCell>{h.polozkaNazev}</TableCell>
                                                <TableCell>{formatDate(h.datumVydeje)}</TableCell>
                                                <TableCell>{formatDate(h.datumVraceni)}</TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={h.stavPriVraceni ?? '—'}
                                                        size="small"
                                                        color={h.stavPriVraceni === 'OK' ? 'success' : h.stavPriVraceni === 'ZTRACENA' ? 'error' : 'warning'}
                                                        variant="outlined"
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Collapse>
                    </Box>
                )}
            </Paper>

            {/* Výdej dialog */}
            <Dialog open={vydejDialogOpen} onClose={() => setVydejDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Vydat prostředek členu</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        <Autocomplete
                            options={volneProVydej}
                            getOptionLabel={(p: InventarPolozka) => `${p.nazev}${p.velikost ? ` (${p.velikost})` : ''} — ${p.kategorieNazev}`}
                            onChange={(_, p) => setVydejPolozkaId(p?.id ?? null)}
                            renderInput={(params) => <TextField {...params} label="Položka k vydání" required />}
                        />
                        <TextField
                            label="Datum výdeje"
                            type="date"
                            value={vydejDatum}
                            onChange={(e) => setVydejDatum(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                        />
                        <FormControl required>
                            <InputLabel>Stav při výdeji</InputLabel>
                            <Select value={vydejStav} onChange={(e) => setVydejStav(e.target.value)} label="Stav při výdeji">
                                <MenuItem value="NOVA">Nová</MenuItem>
                                <MenuItem value="V_POUZIVANI">V používání</MenuItem>
                                <MenuItem value="OPOTREBENA">Opotřebená</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setVydejDialogOpen(false)}>Zrušit</Button>
                    <Button variant="contained" onClick={handleVydej} disabled={!vydejPolozkaId || vydejMutation.isPending}>
                        Vydat
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Vrácení dialog */}
            <Dialog open={!!vraceniPolozkaId} onClose={() => setVraceniPolozkaId(null)} maxWidth="sm" fullWidth>
                <DialogTitle>Vrátit prostředek</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        <TextField
                            label="Datum vrácení"
                            type="date"
                            value={vraceniDatum}
                            onChange={(e) => setVraceniDatum(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                        />
                        <FormControl required>
                            <InputLabel>Stav při vrácení</InputLabel>
                            <Select value={vraceniStav} onChange={(e) => setVraceniStav(e.target.value)} label="Stav při vrácení">
                                <MenuItem value="OK">V pořádku</MenuItem>
                                <MenuItem value="OPOTREBENA">Opotřebená</MenuItem>
                                <MenuItem value="POSKOZENA">Poškozená</MenuItem>
                                <MenuItem value="ZTRACENA">Ztracená</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setVraceniPolozkaId(null)}>Zrušit</Button>
                    <Button variant="contained" color="warning" onClick={handleVraceni} disabled={vraceniMutation.isPending}>
                        Vrátit
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default VystrojniKartaSection;
